import {
  ApolloError,
  FetchPolicy,
  useApolloClient,
  useMutation
} from '@apollo/client'
import { useEffect, useMemo, useState } from 'react'
import { AddMarketCacheUpdate } from '../../../../apollo/cacheModify/Project/addMarket'
import { RemoveMarketCacheUpdate } from '../../../../apollo/cacheModify/Project/removeMarket'
import { RenameMarketCacheUpdate } from '../../../../apollo/cacheModify/Project/renameMarket'
import { WavesQueryVariables } from '../../../../data/gql-gen/dashboard/graphql'
import { getQuestionnaireFragmentData } from '../../../../data/gql-gen/questionnaire'
import { FIELDWORK_FRAGMENT } from '../../../../data/gql/questionnaire/mutations/createOrUpdateFieldworkV2'
import {
  QUESTIONNAIRE,
  QuestionnaireData
} from '../../../../data/gql/questionnaire/queries'
import { GET_FIELDWORK } from '../../../../data/gql/questionnaire/queries/fieldwork'
import { LoggerErrorType } from '../../../../data/logger'
import { EntryType } from '../../../../data/model/questionnaire'
import {
  useCreateFieldworkV2,
  useUpdateFieldworkV2
} from '../../../../hooks/audience/createOrUpdateFieldworkV2'
import useGetSurveysProgress from '../../../../hooks/useGetSurveysProgress'
import useGetUserAndAccount from '../../../../hooks/useGetUserAndAccount'
import { useOptionalWaveId } from '../../../../hooks/useOptionalWaveId'
import { WAVES, WavesData } from '../../../../modules/Project/Project.query'
import { flattenEntries } from '../../../../modules/Questionnaire/Questionnaire.utils'
import { captureApolloError } from '../../../../utils/HelperFunctions'
import {
  PanelSupplier,
  convertAudienceType
} from '../../../../utils/audienceHelper'
import {
  CREATE_SURVEY,
  REMOVE_SURVEY_FROM_PROJECT,
  RENAME_SURVEY
} from './ProjectPage.mutations'

// Entry types that are NOT counted as questions on the project summary: forks
// are routing logic, and sections are containers (their nested items are
// counted instead — flattenEntries surfaces them). Everything else counts.
const NON_QUESTION_ENTRY_TYPES: ReadonlyArray<EntryType> = [
  EntryType.ForkEntryType,
  EntryType.SectionEntryType
]

export interface AddMarketArgs {
  name: string
  countryCode: string
  countryLanguage: string
  // Per-language sample split (fractions summing to 1.0). A single-language
  // market is one row at 1.0.
  languageAllocations: { language: string; percent: number }[]
  completes: number
  // Preset audience for the market. CreateFieldworkV2Input has no preset
  // field, so it is applied via a follow-up updateFieldworkV2 once the
  // fieldwork exists. Absent for the default custom audience.
  presetAudienceId?: string
}

/**
 * Creates a market under the project: `createSurvey` for the survey, then
 * `createFieldworkV2` for its country/language and target completes (the same
 * fieldwork shape used when the master survey is created), then — when a
 * preset audience was chosen — `updateFieldworkV2` to apply it.
 *
 * `createFieldworkV2` returns the full fieldwork, so we seed the per-market
 * fieldwork query cache from that response and only THEN append the market to
 * the cached project. Doing it in that order means the markets table
 * (`useMarketsCompletes`, cache-first on the project page) gains the new
 * surveyId and its language/target together, in a single render pass — no
 * project refetch and no follow-up per-market fieldwork fetch. Returns the new
 * market's surveyId.
 */
export const useAddMarket = (projectId: string, waveId?: string) => {
  const client = useApolloClient()
  const [createSurvey] = useMutation(CREATE_SURVEY, {
    context: { clientName: 'dashboard' },
    onError: (error) => {
      captureApolloError(LoggerErrorType.ApolloMutation, 'createSurvey', error)
    }
  })
  // Used only to roll back a survey whose fieldwork creation failed (see
  // addMarket). No cache update: the orphan was never added to the cached
  // project (AddMarketCacheUpdate runs only after fieldwork succeeds).
  const [removeSurveyFromProject] = useMutation(REMOVE_SURVEY_FROM_PROJECT, {
    context: { clientName: 'dashboard' }
  })
  const { createFieldworkV2 } = useCreateFieldworkV2()
  const { updateFieldworkV2 } = useUpdateFieldworkV2()
  const { markup } = useGetUserAndAccount()
  const [loading, setLoading] = useState(false)

  const addMarket = async ({
    name,
    countryCode,
    countryLanguage,
    languageAllocations,
    completes,
    presetAudienceId
  }: AddMarketArgs) => {
    setLoading(true)
    try {
      const result = await createSurvey({
        variables: { name, projectId, waveId }
      })
      const survey = result.data?.createSurvey
      if (!survey?.surveyId) {
        throw new Error('createSurvey returned no surveyId')
      }
      const { surveyId } = survey

      try {
        const fieldworkResult = await createFieldworkV2({
          variables: {
            input: {
              surveyId,
              countryCode,
              countryLanguage,
              // CreateFieldworkV2Input takes the allocations directly (no
              // tri-state wrapper — that's only on the update mutation).
              languageAllocations,
              samplesNeeded: completes,
              accountMarkup: markup,
              panelSupplierCode: PanelSupplier.PURESPECTRUM
            }
          },
          // Prime the fieldwork query cache with the mutation's own response so
          // the markets table reads this market's language & sample size straight
          // from it (cache-first) instead of re-fetching.
          update(cache, { data }) {
            if (data?.createFieldworkV2) {
              cache.writeQuery({
                query: GET_FIELDWORK,
                variables: { surveyId },
                data: {
                  __typename: 'ConfigQueries',
                  // Unmasks the mutation's ...Fieldwork result to FieldworkFragment,
                  // which only stays assignable here while the fragment and
                  // GET_FIELDWORK select the same fields — so drift is a compile
                  // error, not a silent partial cache write.
                  fieldwork: getQuestionnaireFragmentData(
                    FIELDWORK_FRAGMENT,
                    data.createFieldworkV2
                  )
                }
              })
            }
          }
        })
        // useCreateFieldworkV2 configures an onError handler, so on failure
        // Apollo RESOLVES the mutate promise with { data: undefined, errors }
        // rather than rejecting. Inspect the payload explicitly (mirroring the
        // createSurvey guard above) and treat a missing fieldwork as a failure
        // so the rollback below runs. The catch also still covers a genuine
        // rejection, should that onError handler ever be removed.
        if (!fieldworkResult.data?.createFieldworkV2) {
          throw new Error('createFieldworkV2 returned no fieldwork')
        }

        // A preset audience can't be set at creation (CreateFieldworkV2Input
        // has no preset field), so it is applied as a follow-up update — still
        // inside this try, so a failure rolls the whole market back rather
        // than leaving one that silently differs from what the dialog showed.
        // The response carries the same Fieldwork fragment (normalized by
        // surveyId), so the preset merges into the fieldwork cache primed by
        // the create above.
        if (presetAudienceId) {
          const presetResult = await updateFieldworkV2({
            variables: {
              input: {
                surveyId,
                // Echoes the supplier just created above; switching happens
                // later, in Market settings.
                panelSupplierCode: PanelSupplier.PURESPECTRUM,
                presetAudienceId
              }
            }
          })
          // useUpdateFieldworkV2 also resolves (not rejects) on error — same
          // contract as createFieldworkV2 above. The message reaches the user
          // verbatim via the project page's snackbar, so it explains the
          // rollback rather than naming the mutation.
          if (!presetResult.data?.updateFieldworkV2) {
            throw new Error(
              'Could not apply the preset audience — the market was not created. Please try again.'
            )
          }
        }
      } catch (fieldworkError) {
        // createSurvey + createFieldworkV2 (+ the preset update) are not
        // atomic: the survey now exists on the backend but was never added to
        // the cached project. Roll the orphan survey back so a retry
        // re-creates it cleanly instead of leaving a fieldwork-less market or
        // one missing its preset audience. Best-effort — if rollback fails we
        // log it and still surface the original error to the caller.
        try {
          await removeSurveyFromProject({ variables: { surveyId, projectId } })
        } catch (rollbackError) {
          captureApolloError(
            LoggerErrorType.ApolloMutation,
            'removeSurveyFromProject (addMarket rollback)',
            rollbackError as ApolloError
          )
        }
        throw fieldworkError
      }

      // Append the market to the cached project only now that its fieldwork is
      // in cache, so the markets list gains the new surveyId and its data in the
      // same pass (avoids an empty-then-filled flash / stale row).
      AddMarketCacheUpdate(client.cache, projectId, survey)

      // A wave's markets come from the waves query, not the project, so append
      // it there too — otherwise the wave's market tabs miss what was just
      // added until the next refetch.
      if (waveId) {
        client.cache.updateQuery<WavesData, WavesQueryVariables>(
          { query: WAVES, variables: { projectId } },
          (cached) =>
            cached?.waves
              ? {
                  waves: cached.waves.map((wave) =>
                    wave.waveId === waveId
                      ? { ...wave, surveys: [...wave.surveys, survey] }
                      : wave
                  )
                }
              : cached
        )
      }

      return surveyId
    } finally {
      setLoading(false)
    }
  }

  return { addMarket, loading }
}

export const useRemoveMarket = (
  projectId: string,
  onCompleted?: () => void | Promise<unknown>
) => {
  const [removeSurveyFromProject, { loading }] = useMutation(
    REMOVE_SURVEY_FROM_PROJECT,
    {
      context: { clientName: 'dashboard' },
      // Drop the removed market from the cached project so every reader stays
      // consistent without a refetch — the project page relies on its own
      // refetch, but the settings drawer navigates back to the (cache-first)
      // project page, where the removed row would otherwise linger.
      update: (cache, _result, { variables }) => {
        const removedSurveyId = variables?.surveyId as string | undefined
        if (removedSurveyId) {
          RemoveMarketCacheUpdate(cache, projectId, removedSurveyId)
        }
      }
      // No `onError` here on purpose: an onError callback makes Apollo resolve
      // (not reject) the mutate promise, so callers could never tell a removal
      // failed — the dialog would close / the drawer would navigate away even
      // on error. We log + rethrow in removeMarket below instead.
    }
  )

  const removeMarket = async (surveyId: string) => {
    try {
      await removeSurveyFromProject({ variables: { surveyId, projectId } })
    } catch (error) {
      // The mutate promise rejects with an ApolloError.
      captureApolloError(
        LoggerErrorType.ApolloMutation,
        'removeSurveyFromProject',
        error as ApolloError
      )
      throw error
    }
    await onCompleted?.()
  }

  return { removeMarket, loading }
}

export const useRenameMarket = (projectId: string) => {
  const [renameSurvey, { loading }] = useMutation(RENAME_SURVEY, {
    context: { clientName: 'dashboard' },
    // Patch from the server's Survey, not the requested name, so the cached
    // name always matches what was stored (a normalised name would otherwise
    // never converge and every blur would re-send the rename).
    update: (cache, result) => {
      const renamed = result.data?.renameSurvey
      if (renamed?.surveyId && renamed?.name) {
        RenameMarketCacheUpdate(
          cache,
          projectId,
          renamed.surveyId,
          renamed.name
        )
      }
    }
    // No `onError` here on purpose (same as useRemoveMarket above): an onError
    // callback makes Apollo resolve the mutate promise, so the drawer could
    // never tell the rename failed and revert its field.
  })

  const renameMarket = async (surveyId: string, name: string) => {
    try {
      await renameSurvey({ variables: { surveyId, name } })
    } catch (error) {
      captureApolloError(
        LoggerErrorType.ApolloMutation,
        'renameSurvey',
        error as ApolloError
      )
      throw error
    }
  }

  return { renameMarket, loading }
}

export interface MarketCompletes {
  /** Respondents collected so far. 0 for a market that has not fielded. */
  collected: number
  /** Target completes the market was created with (fieldwork samplesNeeded). */
  target: number
}

/**
 * Per-market completes for the markets table and the project total. The target
 * lives in the questionnaire service's fieldwork (`samplesNeeded`), not on the
 * dashboard Survey, so it is fetched per market via `fieldworkV2`; the collected
 * count is fieldwork state rather than survey definition, so it comes from the
 * responses service's progress endpoint instead (see `useGetSurveysProgress`).
 */
interface MarketSummary {
  surveyId: string
  /** Target completes the market was created with (fieldwork samplesNeeded). */
  target: number
  /** Languages this market is fielded in (allocations, else its locale). */
  languages: string[]
  /** ISO country code of the market's locale, e.g. "AR" (upper-cased). */
  country: string
  /** Panel supplier of the market's audience (for fieldwork mutations). */
  panelSupplier?: PanelSupplier
}

export const useMarketsCompletes = (
  surveyIds: string[],
  // The project page passes 'cache-first' so a freshly added market renders
  // from the fieldwork the create mutation already put in cache (see
  // useAddMarket). Other consumers keep 'network-only' for always-fresh reads.
  fetchPolicy: FetchPolicy = 'network-only'
) => {
  const client = useApolloClient()
  const [summaries, setSummaries] = useState<MarketSummary[]>([])
  const [loading, setLoading] = useState(false)
  const key = surveyIds.join(',')
  // `undefined` rather than `[]` before the project resolves: an empty list
  // would still post a progress request for no surveys.
  const { surveyProgress } = useGetSurveysProgress(
    surveyIds.length > 0 ? surveyIds : undefined
  )

  useEffect(() => {
    const ids = key ? key.split(',') : []
    if (ids.length === 0) {
      setSummaries([])
      return
    }

    let cancelled = false
    setLoading(true)

    Promise.all(
      ids.map(async (surveyId): Promise<MarketSummary> => {
        try {
          const { data } = await client.query({
            query: GET_FIELDWORK,
            variables: { surveyId },
            context: { clientName: 'questionnaire' },
            fetchPolicy
          })
          const fieldwork = data.fieldwork
          const samples = fieldwork?.samplesNeeded
          const target = samples?.unadjusted ?? samples?.adjusted ?? 0
          const allocated = (fieldwork?.languageAllocations ?? []).map(
            (allocation) => allocation.language
          )
          const localeLanguage = fieldwork?.locale.language
          const languages =
            allocated.length > 0
              ? allocated
              : localeLanguage
                ? [localeLanguage]
                : []
          const country = (fieldwork?.locale.country ?? '').toUpperCase()
          const audienceType = fieldwork?.audience[0]?.__typename
          const panelSupplier = audienceType
            ? convertAudienceType(audienceType)
            : undefined
          return {
            surveyId,
            target,
            languages,
            country,
            panelSupplier
          }
        } catch {
          return {
            surveyId,
            target: 0,
            languages: [],
            country: ''
          }
        }
      })
    ).then((results) => {
      if (cancelled) {
        return
      }
      setSummaries(results)
      setLoading(false)
    })

    return () => {
      cancelled = true
    }
  }, [key, client, fetchPolicy])

  // A market with no progress yet (draft, or launched but nothing in) has no
  // entry (or a null `progress`) in the response, which reads as 0 collected.
  const collectedBySurveyId = useMemo(
    () =>
      new Map<string, number>(
        (surveyProgress ?? []).map(({ surveyId, progress }) => [
          surveyId,
          progress?.samplesCollected ?? 0
        ])
      ),
    [surveyProgress]
  )
  const completesBySurveyId = useMemo(
    () =>
      new Map<string, MarketCompletes>(
        summaries.map(({ surveyId, target }) => [
          surveyId,
          { collected: collectedBySurveyId.get(surveyId) ?? 0, target }
        ])
      ),
    [summaries, collectedBySurveyId]
  )
  const languagesBySurveyId = useMemo(
    () =>
      new Map<string, string[]>(
        summaries.map(({ surveyId, languages }) => [surveyId, languages])
      ),
    [summaries]
  )
  const countryBySurveyId = useMemo(
    () =>
      new Map<string, string>(
        summaries.map(({ surveyId, country }) => [surveyId, country])
      ),
    [summaries]
  )
  const panelSupplierBySurveyId = useMemo(
    () =>
      new Map<string, PanelSupplier | undefined>(
        summaries.map(({ surveyId, panelSupplier }) => [
          surveyId,
          panelSupplier
        ])
      ),
    [summaries]
  )
  const totalCollected = summaries.reduce(
    (sum, { surveyId }) => sum + (collectedBySurveyId.get(surveyId) ?? 0),
    0
  )
  const totalTarget = summaries.reduce((sum, { target }) => sum + target, 0)
  // Distinct languages across all markets (e.g. Canada + Belgium → one French).
  const languageCount = new Set(summaries.flatMap(({ languages }) => languages))
    .size

  return {
    completesBySurveyId,
    languagesBySurveyId,
    countryBySurveyId,
    panelSupplierBySurveyId,
    totalCollected,
    totalTarget,
    languageCount,
    loading
  }
}

/**
 * Number of survey questions in the project, read from the master survey's
 * draft questionnaire (the questionnaire is shared across all markets).
 */
export const useMasterQuestionsCount = (
  projectId: string,
  masterSurveyId?: string
) => {
  const client = useApolloClient()
  const waveId = useOptionalWaveId()
  const [count, setCount] = useState(0)

  useEffect(() => {
    if (!masterSurveyId) {
      setCount(0)
      return
    }

    let cancelled = false

    client
      .query<QuestionnaireData>({
        query: QUESTIONNAIRE,
        variables: { projectId, surveyId: masterSurveyId, waveId },
        context: { clientName: 'questionnaire' },
        fetchPolicy: 'network-only'
      })
      .then(({ data }) => {
        if (cancelled) {
          return
        }
        // Sum of survey questions (entries) and audience/screening questions
        // (audienceEntries). flattenEntries expands sections into their nested
        // items so those are counted; forks and section wrappers are excluded.
        const allEntries = flattenEntries([
          ...data.draftQuestionnaire.entries,
          ...data.draftQuestionnaire.audienceEntries
        ])
        setCount(
          allEntries.filter(
            (entry) => !NON_QUESTION_ENTRY_TYPES.includes(entry.entryType)
          ).length
        )
      })
      .catch(() => {
        if (!cancelled) {
          setCount(0)
        }
      })

    return () => {
      cancelled = true
    }
  }, [client, projectId, masterSurveyId, waveId])

  return count
}
